import { Button } from '@/components/ui/button';
import { signup_path } from '@/rails/routes';
import type { TemplateSummary } from '@/types/platform';
import { Link } from '@inertiajs/react';
import type { ReactNode } from 'react';

const SECTION = 'border-t py-14 sm:py-20';
const CONTAINER = 'mx-auto max-w-6xl px-4 sm:px-6';

interface Props {
  templates: TemplateSummary[];
  signupOpen: boolean;
}

interface EyebrowProps {
  children: ReactNode;
}

// The bronze is the rule only. At this size bronze text on paper is too faint to read.
const Eyebrow = ({ children }: EyebrowProps) => (
  <div className="flex items-center gap-3">
    <span className="bg-accent h-px w-6 shrink-0" aria-hidden="true" />
    <span className="text-muted-foreground text-xs font-medium tracking-[0.18em] uppercase">
      {children}
    </span>
  </div>
);

const Index = ({ templates, signupOpen }: Props) => {
  const signupLabel = signupOpen ? 'Get started' : 'Request access';

  return (
    <div className="bg-background text-foreground">
      <section className={`${CONTAINER} py-14 sm:py-20`}>
        <div className="flex max-w-2xl flex-col gap-6">
          <Eyebrow>Templates</Eyebrow>
          <h1 className="authority-display text-4xl leading-[1.08] text-balance sm:text-6xl">
            Start from an app that is already laid out.
          </h1>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Each template is a working app with its lists and fields in place. Change them to match
            how you work.
          </p>
        </div>

        {templates.length > 0 ? (
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            {templates.map((template) => (
              <article
                key={template.key}
                id={template.key}
                aria-labelledby={`${template.key}-name`}
                className="bg-card flex scroll-mt-24 flex-col gap-6 border p-5 sm:p-8"
              >
                <header className="flex flex-col gap-3">
                  <h2 id={`${template.key}-name`} className="text-2xl leading-tight tracking-tight">
                    {template.name}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">{template.summary}</p>
                </header>

                {template.industries.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <h3 className="text-muted-foreground text-sm">Made for</h3>
                    <ul className="flex flex-wrap gap-2">
                      {template.industries.map((industry) => (
                        <li key={industry} className="border px-2.5 py-1 text-sm">
                          {industry}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {template.lists.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <h3 className="text-muted-foreground text-sm">Lists and their fields</h3>
                    <dl className="border-t">
                      {template.lists.map((list) => (
                        <div
                          key={list.key}
                          className="grid gap-1 border-b py-3 sm:grid-cols-[9rem_1fr] sm:gap-6"
                        >
                          <dt className="font-medium">{list.name}</dt>
                          <dd className="text-muted-foreground text-sm leading-relaxed">
                            {list.fields.join(', ')}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground mt-12 border-t pt-6">
            There are no templates to show yet.
          </p>
        )}
      </section>

      <section className={SECTION}>
        <div className={`${CONTAINER} flex flex-col gap-6`}>
          <h2 className="max-w-2xl text-3xl leading-tight tracking-tight sm:text-4xl">
            Pick a template when you make your first app, or describe your own.
          </h2>
          <div>
            <Button asChild size="lg">
              <Link href={signup_path()}>{signupLabel}</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Index;
